import { useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { isHttpUrl, isRedirectType, isValidDomain, normalizeDomain } from "@/lib/store";
import type { ImportRow } from "@/lib/store";
import type { RedirectType } from "@/types";

interface BatchImportModalProps {
  open: boolean;
  defaultType: RedirectType;
  existing: string[];
  onClose: () => void;
  onImport: (rows: ImportRow[]) => Promise<{ message: string; added: number }>;
}

interface ParsedLine extends ImportRow {
  error: string;
}

function parseLines(text: string, sharedTarget: string, defaultType: RedirectType, existing: Set<string>): ParsedLine[] {
  const seen = new Set(existing);
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith("#"))
    .slice(0, 200)
    .map((line) => {
      let parts = line.split(/[,，\t]/).map((part) => part.trim()).filter(Boolean);
      if (parts.length === 1 && /\s/.test(parts[0])) parts = parts[0].split(/\s+/);
      const domain = normalizeDomain(parts[0] ?? "");
      const targetUrl = (parts[1] || sharedTarget).trim();
      let redirectType = defaultType;
      let remark = "";
      if (parts[2] && isRedirectType(parts[2])) {
        redirectType = parts[2];
        remark = parts.slice(3).join(" ");
      } else if (parts[2]) {
        remark = parts.slice(2).join(" ");
      }
      let error = "";
      if (!isValidDomain(domain)) error = "域名格式不正确";
      else if (seen.has(domain)) error = "域名已存在";
      else if (!isHttpUrl(targetUrl)) error = "缺少有效的目标网址";
      else seen.add(domain);
      return { domain, targetUrl, redirectType, remark, error };
    });
}

export default function BatchImportModal({ open, defaultType, existing, onClose, onImport }: BatchImportModalProps) {
  const [text, setText] = useState("");
  const [sharedTarget, setSharedTarget] = useState("");
  const [notice, setNotice] = useState("");

  const parsed = useMemo(
    () => (open ? parseLines(text, sharedTarget, defaultType, new Set(existing)) : []),
    [open, text, sharedTarget, defaultType, existing],
  );
  const valid = parsed.filter((row) => !row.error);

  const handleClose = () => {
    setText("");
    setSharedTarget("");
    setNotice("");
    onClose();
  };

  const handleImport = () => {
    if (!valid.length) {
      setNotice("没有可导入的域名");
      return;
    }
    void onImport(valid.map(({ domain, targetUrl, redirectType, remark }) => ({ domain, targetUrl, redirectType, remark }))).then((result) => {
      if (result.added > 0) {
        handleClose();
        return;
      }
      setNotice(result.message);
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      wide
      title="批量导入域名"
      subtitle="每行一条：域名, 目标网址, 跳转方式, 备注。后两项可以省略。"
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleImport}
            className="rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            导入 {valid.length} 条
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-2">
          <span className="font-label text-sm font-medium text-foreground-800">统一目标网址（可选）</span>
          <input
            type="text"
            value={sharedTarget}
            onChange={(event) => setSharedTarget(event.target.value)}
            placeholder="某一行没写网址时，使用这个地址"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-500"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="font-label text-sm font-medium text-foreground-800">域名列表</span>
          <textarea
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setNotice("");
            }}
            rows={7}
            placeholder={"go.example.com, https://example.com/landing, 302, 夏季活动\npromo.example.com, https://example.com/sale, 301"}
            className="resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-500"
          />
          <span className="text-xs text-foreground-500">
            跳转方式只接受 301、302、mask。没写时使用当前默认方式 {defaultType === "mask" ? "隐藏跳转" : defaultType}。
          </span>
        </label>

        {parsed.length > 0 ? (
          <div className="flex max-h-40 flex-col gap-1.5 overflow-y-auto rounded-md bg-background-100 p-3">
            {parsed.slice(0, 12).map((row, index) => (
              <div key={`${row.domain}-${index}`} className="flex items-center justify-between gap-3 text-xs">
                <span className="truncate text-foreground-800">{row.domain || "（空域名）"}</span>
                <span className={row.error ? "shrink-0 text-secondary-800" : "shrink-0 text-accent-800"}>
                  {row.error || "可以导入"}
                </span>
              </div>
            ))}
            {parsed.length > 12 ? <p className="text-xs text-foreground-500">还有 {parsed.length - 12} 行</p> : null}
          </div>
        ) : null}

        {notice ? (
          <div className="flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
            <i className="ri-error-warning-line" />
            {notice}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
